/**
 * Coveristic Encrypted Administrator Security Vault
 * - Stores zero plaintext credentials or email addresses in website source files or compiled bundles.
 * - Uses salted SHA-256 cryptographic digests for username, password, OTP, and recovery code verification.
 * - Dispatches 2-Step OTP and 5-Attempt Recovery/Password-Reset codes across redundant server & browser relays.
 */

const VAULT_SALT = 'COV_SEC_VAULT_v4_961';
const CUSTOM_PASS_HASH_STORAGE_KEY = 'cov_sec_vault_ph_v4';

// Precomputed salted SHA-256 digests (irreversible one-way cryptographic hashes)
const AUTHORIZED_USER_DIGEST = 'cab7f200076ff476f87d898e35eea50ac537a9874991dd406adf53b907196e5b';
const DEFAULT_PASS_DIGEST = '7e16f35c28ea3448d74fc562823586b4ced8aeee0c18bf180fe1a9dfc7c61e98';

// XOR-encrypted recipient cipher bytes (never stored in plaintext in bundle)
const CIPHER_KEY = [0x43, 0x6f, 0x76, 0x65, 0x72, 0x69, 0x73, 0x74, 0x69, 0x63];
const ENCRYPTED_RECIPIENT_BYTES = [
  48, 14, 26, 4, 31, 12, 27, 19, 27, 2, 51, 7, 31, 6, 1, 41, 20, 25, 8, 10, 47, 65, 21, 10, 31
];

let sha256H: number[] = [];
let sha256K: number[] = [];

function rightRotate(value: number, amount: number): number {
  return (value >>> amount) | (value << (32 - amount));
}

/**
 * Pure synchronous SHA-256 implementation (UTF-8 safe, works in all browser contexts)
 */
export function sha256Hex(rawInput: string): string {
  // Encode UTF-8 string to binary ASCII representation
  const utf8Str = unescape(encodeURIComponent(rawInput));
  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let i: number;
  let j: number;
  let result = '';

  const words: number[] = [];
  const asciiBitLength = utf8Str.length * 8;

  let hash = sha256H.slice(0);
  const k = sha256K;
  let primeCounter = k.length;

  if (primeCounter === 0) {
    const isComposite: Record<number, number> = {};
    for (let candidate = 2; primeCounter < 64; candidate++) {
      if (!isComposite[candidate]) {
        for (i = 0; i < 313; i += candidate) {
          isComposite[i] = candidate;
        }
        sha256H[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
        k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
      }
    }
    hash = sha256H.slice(0);
  }

  let padded = utf8Str + '\x80';
  while ((padded.length % 64) - 56) {
    padded += '\x00';
  }

  for (i = 0; i < padded.length; i++) {
    j = padded.charCodeAt(i);
    if (j >> 8) return '';
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;

  for (j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0, 8);
    hash = hash.slice(0, 8);

    for (i = 0; i < 64; i++) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];
      const a = hash[0];
      const e = hash[4];
      const temp1 =
        hash[7] +
        (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
        ((e & hash[5]) ^ (~e & hash[6])) +
        k[i] +
        (w[i] =
          i < 16
            ? w[i]
            : (w[i - 16] +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                w[i - 7] +
                (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
              0);

      const temp2 =
        (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
        ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));

      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.length = 8;
    }

    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

/**
 * Computes the salted SHA-256 digest of a credential or OTP value
 */
export function computeVaultDigest(value: string): string {
  return sha256Hex(`${VAULT_SALT}:${value}`);
}

/**
 * Constant-time string comparison to prevent timing side-channel attacks
 */
function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * Verifies Administrator Username & Password using salted SHA-256 digests only
 */
export function verifyEncryptedAdminCredentials(usernameInput: string, passwordInput: string): boolean {
  const cleanUser = usernameInput.trim();
  const userDigest = computeVaultDigest(cleanUser);
  const passDigest = computeVaultDigest(passwordInput);

  if (!timingSafeEqualHex(userDigest, AUTHORIZED_USER_DIGEST)) {
    return false;
  }

  // Check if a custom password hash was set via verified email recovery
  try {
    const customHash = localStorage.getItem(CUSTOM_PASS_HASH_STORAGE_KEY);
    if (customHash && customHash.length === 64) {
      if (timingSafeEqualHex(passDigest, customHash)) {
        return true;
      }
    }
  } catch {}

  return timingSafeEqualHex(passDigest, DEFAULT_PASS_DIGEST);
}

/**
 * Saves a new administrator password strictly as an irreversible salted SHA-256 digest
 */
export function updateEncryptedAdminPassword(newPassword: string): void {
  const trimmed = newPassword.trim();
  if (!trimmed) return;
  const newDigest = computeVaultDigest(trimmed);
  try {
    localStorage.setItem(CUSTOM_PASS_HASH_STORAGE_KEY, newDigest);
  } catch {}
}

/**
 * Generates a cryptographically random 6-digit numeric code (100000 - 999999)
 */
export function generateSecureSixDigitCode(): string {
  const arr = new Uint32Array(1);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(arr);
  } else {
    arr[0] = Math.floor(Math.random() * 900000) + 100000;
  }
  return String(100000 + (arr[0] % 900000));
}

/**
 * Decrypts the hidden administrator recipient address in memory at dispatch time only
 */
function decryptRecipientInMemory(): string {
  return String.fromCharCode(
    ...ENCRYPTED_RECIPIENT_BYTES.map((byte, idx) => byte ^ CIPHER_KEY[idx % CIPHER_KEY.length])
  );
}

async function fetchWithFastTimeout(url: string, options: RequestInit, timeoutMs: number = 3500): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timer);
    return response;
  } catch (err) {
    clearTimeout(timer);
    throw err;
  }
}

/**
 * Dispatches either a 2-Step Login OTP code or a 5-Failed-Attempts Account Recovery / Password Reset code
 * across multiple redundant delivery channels simultaneously:
 *   1. Live Hosting Mailer on coveristic.net (/api/contact.php & /api/auth.php)
 *   2. Local Server Relay (/api/auth.php & /api/contact.php)
 *   3. FormSubmit AJAX Relay (with fast timeout so Cloudflare 522 outages never block login)
 */
export async function dispatchAdminSecurityEmail(
  code: string,
  purpose: 'otp' | 'recovery' = 'otp'
): Promise<boolean> {
  const recipient = decryptRecipientInMemory();
  const timestamp = new Date().toUTCString();

  const subject =
    purpose === 'recovery'
      ? `Coveristic Security Alert: Admin Recovery & Password Reset Code [${code}]`
      : `Coveristic Admin 2-Step Verification OTP Code: ${code}`;

  const messageBody =
    purpose === 'recovery'
      ? `SECURITY LOCKOUT & ACCOUNT RECOVERY ALERT\n\n5 failed login attempts were detected on your Coveristic Admin Portal.\n\nYour 6-Digit Account Recovery & Password Reset Code is:\n\n>>> ${code} <<<\n\nEnter this 6-digit code on the Admin Recovery screen to unlock your account, optionally set a new password, and sign in safely.\n\nRequested at: ${timestamp}\nValid for: 15 minutes (Single-Use)`
      : `COVERISTIC ADMIN 2-STEP VERIFICATION\n\nYour single-use 6-Digit Admin Login OTP Code is:\n\n>>> ${code} <<<\n\nEnter this 6-digit code on the Coveristic Admin Login page to access the Admin Portal.\n\nRequested at: ${timestamp}\nValid for: 10 minutes (Single-Use)`;

  const contactPayload = JSON.stringify({
    name: 'Coveristic Security Vault',
    email: 'noreply@coveristic.net',
    phone: '+96181773349',
    city: 'Security Dispatch',
    subject,
    message: messageBody
  });

  const authPayload = JSON.stringify({
    action: purpose === 'recovery' ? 'send_recovery' : 'send_2fa',
    purpose,
    code
  });

  const tasks: Promise<boolean>[] = [
    // Channel 1: Local/Same-Origin /api/auth.php
    fetchWithFastTimeout(
      '/api/auth.php',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: authPayload
      },
      4000
    )
      .then(async r => {
        if (!r.ok) return false;
        const d = await r.json().catch(() => ({}));
        return Boolean(d && (d.mail_sent || d.relay_sent || d.success));
      })
      .catch(() => false),

    // Channel 2: Live Production Hosting Mailer (https://coveristic.net/api/contact.php)
    // Uses the verified working PHP mail() dispatcher on coveristic.net
    fetchWithFastTimeout(
      'https://coveristic.net/api/contact.php',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: contactPayload
      },
      4000
    )
      .then(async r => {
        if (!r.ok) return false;
        const d = await r.json().catch(() => ({}));
        return Boolean(d && d.success);
      })
      .catch(() => false),

    // Channel 3: Live Production Hosting Auth Endpoint (https://coveristic.net/api/auth.php)
    fetchWithFastTimeout(
      'https://coveristic.net/api/auth.php',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: authPayload
      },
      4000
    )
      .then(async r => {
        if (!r.ok) return false;
        const d = await r.json().catch(() => ({}));
        return Boolean(d && d.success);
      })
      .catch(() => false),

    // Channel 4: FormSubmit.co AJAX with standard name + email + message fields and fast 3.5s timeout
    fetchWithFastTimeout(
      `https://formsubmit.co/ajax/${recipient}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: 'Coveristic Security Vault',
          email: recipient,
          _subject: subject,
          message: messageBody,
          security_code: code,
          requested_at: timestamp
        })
      },
      3500
    )
      .then(async r => {
        if (!r.ok) return false;
        const d = await r.json().catch(() => ({}));
        return Boolean(d && (d.success === 'true' || d.success === true));
      })
      .catch(() => false)
  ];

  const results = await Promise.all(tasks);
  return results.some(Boolean);
}
